import type { Maybe } from '@adornis/base/utilTypes.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-components/bh-stat-card.js';
import {
  getAverageParticipationsPerUser,
  getParticipantsCount,
  getParticipationCount,
  getParticipationCountGroupedByTimeframeState,
  getParticipationsOverTime,
  getUserCount,
  getUserProductRegistrationsOverTime,
} from '@adornis/buchungsheld-db/api/statistic.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import '@adornis/charts/apexcharts/client/apex-chart.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-section.js';
import '@adornis/internationalization/client/x-tel-input.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { from } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { BHFilter } from '../../entities/bh-filter.js';
import { Institution } from '../../entities/institution.js';
import { ParticipationState } from '../../enums/enums.js';
import { Role } from '../../enums/roles.js';
import { translations } from '../../translations/translation.js';
import { isDefined } from '../../util.js';
import type { InstitutionStatisticsPlugin } from '../plugin-types.js';
import { getApexLineChartOptions, getApexStackedBarChartOptions } from './chart-helpers.js';

export const fromPromiseStartWithNull = () => switchMap(p => from(p).pipe(startWith(null)));

@customElement('global-instititution-statistics')
export class GlobalInstitutionStatistics extends ChemistryLitElement {
  @property({ attribute: 'filter' }) filter = new RXController<BHFilter | null>(this, null) as RXController<BHFilter>;

  protected readonly _filter = this.filter.observable.pipe(filter(isDefined));

  @state() protected readonly _translation = new TranslationController<typeof translations>(this);
  @state() protected readonly _size = new WindowSizeController(this);

  @state() private readonly adminCount = new RXController(
    this,
    from(getUserCount([Role.ADMIN, Role.SUPER_ADMIN])()).pipe(startWith(null)),
  );

  @state() private readonly participantCount = new RXController(
    this,
    this._filter.pipe(
      map(filter => getParticipantsCount(filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly institutionCount = new RXController(
    this,
    from(Institution.count()()).pipe(startWith(null)),
  );

  @state() protected readonly workshopCount = new RXController(
    this,
    this._filter.pipe(
      switchMap(filter =>
        from(Workshop.countMatchingProducts(filter.institutionID, filter.timeframeID)()).pipe(startWith(null)),
      ),
    ),
  );

  @state() protected readonly participationParticipationCount = new RXController(
    this,
    this._filter.pipe(
      map(filter => getParticipationCount([ParticipationState.IN], filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly participatedParticipationCount = new RXController(
    this,
    this._filter.pipe(
      map(filter => getParticipationCount([ParticipationState.PARTICIPATED], filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly participationAndParticipatedParticipationCount = new RXController(
    this,
    this._filter.pipe(
      map(filter => getParticipationCount([ParticipationState.IN, ParticipationState.PARTICIPATED], filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly suspendedAndSignedOffAndWaitingParticipationCount = new RXController(
    this,
    this._filter.pipe(
      map(filter =>
        getParticipationCount(
          [ParticipationState.SUSPENDED, ParticipationState.LEFT, ParticipationState.WAITING],
          filter,
        )(),
      ),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly terminCount = new RXController(
    this,
    this._filter.pipe(
      map(filter =>
        Promise.all([
          Einzelberatung.getEinzelberatungSlotCount(filter.institutionID, filter.timeframeID)(),
          Workshop.getAppointmentCount(filter.institutionID, filter.timeframeID)(),
        ]).then(([einzelberatungTerminCount, workshopTerminCount]) => einzelberatungTerminCount + workshopTerminCount),
      ),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly averageParticipationAndParticipatedParticipationsPerUser = new RXController(
    this,
    this._filter.pipe(
      map(filter =>
        getAverageParticipationsPerUser([ParticipationState.IN, ParticipationState.PARTICIPATED], filter)(),
      ),
      fromPromiseStartWithNull(),
      map(p => p?.toFixed(2)?.replace(',', '')?.replace('.', ',')),
    ),
  );

  @state() protected readonly averageSuspendedAndSignedOffAndWaitingParticipationsPerUser = new RXController(
    this,
    this._filter.pipe(
      map(filter =>
        getAverageParticipationsPerUser(
          [ParticipationState.SUSPENDED, ParticipationState.LEFT, ParticipationState.WAITING],
          filter,
        )(),
      ),
      fromPromiseStartWithNull(),
      map(p => p?.toFixed(2)?.replace(',', '')?.replace('.', ',')),
    ),
  );

  @state() protected readonly productRegistrationsOverTime = new RXController(
    this,
    this._filter.pipe(
      map(filter => getUserProductRegistrationsOverTime(filter, 50)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly participationsOverTime = new RXController(
    this,
    this._filter.pipe(
      map(filter => getParticipationsOverTime(filter, 50)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() protected readonly terminParticipation = new RXController(
    this,
    this._filter.pipe(
      map(filter =>
        getParticipationCountGroupedByTimeframeState(filter)({ timeframe: Timeframe.allFields, counts: 1 }),
      ),
      fromPromiseStartWithNull(),
    ),
  );

  maybeRenderCard(name: string, c: RXController<any>) {
    const cardContent = (() => {
      if (c.value === null || c.value === undefined)
        return html`<x-loader ${css({ width: '1rem', height: '1rem' })}></x-loader>`;
      return c.value;
    })();
    return html`<bh-stat-card name="${name}" .value=${cardContent}></bh-stat-card>`;
  }

  renderPieChartCard(name: string, series: Maybe<number[]>, labels: Maybe<string[]>, multicolumn: boolean = false) {
    const c = this.colors;
    const _colors = [
      c.accent,
      c.secondary,
      c.tone.success,
      c.tone.warning,
      c.tone.error,
      { string: '#1fa384' },
      { string: '#4474b3' },
    ];
    const colors = _colors.map(c => c.string);

    const cardContent = (() => {
      if (series === null || series === undefined || labels === null || labels === undefined)
        return html`<x-loader></x-loader>`;
      return html` <apex-chart
        .options=${{
          series,
          labels,
          colors: series.map((_, i) => colors[i % colors.length]),
          chart: {
            type: 'pie',
          },
        }}
      ></apex-chart>`;
    })();

    return html`
      <bh-stat-card
        page-break
        advanced
        ${css(multicolumn ? { gridColumn: this._size.width < 1500 ? 'span 1' : 'span 2' } : {})}
        name=${name}
        .value=${cardContent}
      ></bh-stat-card>
    `;
  }

  renderInstitutionsCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'reg_institutions') as string,
      this.institutionCount,
    );
  }

  renderAdminsCard() {
    return this.maybeRenderCard(this._translation.translate('statistics', 'reg_admins') as string, this.adminCount);
  }

  renderParticipantsCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'reg_participants') as string,
      this.participantCount,
    );
  }

  renderTotalWorkshopsCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'total_workshops') as string,
      this.workshopCount,
    );
  }

  renderParticipatingParticipationCountCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'active_participations') as string,
      this.participationParticipationCount,
    );
  }

  renderParticipatedParticipationCountCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'completed_participations') as string,
      this.participatedParticipationCount,
    );
  }

  // renderParticipatingAndParticipatedParticipationCountCard() {
  //   return this.maybeRenderCard(
  //     this._translation.translate('statistics', 'active_and_completed_participations') as string,
  //     this.participationAndParticipatedParticipationCount,
  //   );
  // }

  renderSuspendedAndSignedOffAndWaitingParticipationCountCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'cancled_and_suspened_and_waiting_participations') as string,
      this.suspendedAndSignedOffAndWaitingParticipationCount,
    );
  }

  renderTotalAppointmentsCard() {
    return this.maybeRenderCard(
      this._translation.translate('statistics', 'appointments_total') as string,
      this.terminCount,
    );
  }

  renderAverageParticipationAndParticipatedParticipationsPerUserCard() {
    return this.maybeRenderCard(
      this._translation.translate(
        'statistics',
        'average_participating_and_participated_participations_per_user',
      ) as string,
      this.averageParticipationAndParticipatedParticipationsPerUser,
    );
  }

  renderAverageSuspendedAndSignedOffAndWaitingParticipationsPerUser() {
    return this.maybeRenderCard(
      this._translation.translate(
        'statistics',
        'average_suspended_and_signed_off_and_waiting_participations_per_user',
      ) as string,
      this.averageSuspendedAndSignedOffAndWaitingParticipationsPerUser,
    );
  }

  renderProductRegistrationsOverTimeCard() {
    const cardContent = (() => {
      if (!this.productRegistrationsOverTime.value) return html`<x-loader></x-loader>`;
      return html`
        <apex-chart
          .options=${getApexLineChartOptions(
            this,
            (this.productRegistrationsOverTime.value ?? []).map(([week, total]) => total ?? 0),
            (this.productRegistrationsOverTime.value ?? []).map(([week, total]) => 'KW' + week),
            'Nutzer',
          )}
        ></apex-chart>
      `;
    })();

    return html`<bh-stat-card
      page-break
      advanced
      name=${this._translation.translate('statistics', 'product_registrations_over_time')}
      .value=${cardContent}
    ></bh-stat-card> `;
  }

  renderParticipationOverTimeCard() {
    const cardContent = (() => {
      if (!this.participationsOverTime.value) return html`<x-loader></x-loader>`;
      return html`
        <apex-chart
          .options=${getApexLineChartOptions(
            this,
            (this.participationsOverTime.value ?? []).map(([week, total]) => total ?? 0),
            (this.participationsOverTime.value ?? []).map(([week, total]) => 'KW' + week),
            'Teilnahmen',
          )}
        ></apex-chart>
      `;
    })();
    return html`<bh-stat-card
      page-break
      advanced
      name=${this._translation.translate('statistics', 'workshop_participation_over_time')}
      .value=${cardContent}
    ></bh-stat-card> `;
  }

  renderParticipationsCountPerStateCard() {
    const c = this.colors;
    const _colors = [
      c.accent,
      c.secondary,
      c.tone.success,
      c.tone.warning,
      c.tone.error,
      { string: '#1fa384' },
      { string: '#4474b3' },
    ];
    const colors = _colors.map(c => c.string);

    const cardContent = (() => {
      if (!this.terminParticipation.value) return html`<x-loader></x-loader>`;
      const terminParticipationWithTimeframe = (this.terminParticipation.value ?? []).filter(d => d.timeframe);
      return html` <apex-chart
        .options=${getApexStackedBarChartOptions(
          this,
          Object.entries(translations.WORKSHOPS.STATES).map(([key, translations], i) => ({
            name: translations[0],
            data: terminParticipationWithTimeframe.map(d => d.counts.find(([state, value]) => state === key)?.[1] ?? 0),
            color: colors[i % colors.length],
          })),
          terminParticipationWithTimeframe.map(d => d.timeframe.name),
        )}
      ></apex-chart>`;
    })();

    return html`
      <bh-stat-card
        advanced
        ${css({ gridColumn: this._size.width < 1500 ? 'span 1' : 'span 2' })}
        name=${this._translation.translate('statistics', 'participation_count_per_bookingstate')}
        .value=${cardContent}
      ></bh-stat-card>
    `;
  }

  protected chartCards() {
    return [
      this.renderInstitutionsCard(),
      this.renderAdminsCard(),
      this.renderParticipantsCard(),
      this.renderTotalWorkshopsCard(),
      this.renderParticipatingParticipationCountCard(),
      this.renderParticipatedParticipationCountCard(),
      // this.renderParticipatingAndParticipatedParticipationCountCard(),
      this.renderTotalAppointmentsCard(),
      this.renderParticipationOverTimeCard(),
      this.renderParticipationsCountPerStateCard(),
    ];
  }

  override render() {
    return html` <x-grid columns=${this._size.width < 1500 ? '1' : '2'} space="md"> ${this.chartCards()} </x-grid> `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        '@media print': {
          'bh-stat-card[page-break]': {
            breakAfter: 'page',
          },
          'bh-stat-card[page-break] + bh-stat-card': {
            marginTop: '4rem',
          },
        },
      },
    ];
  }
}

@customElement('default-institution-statistics')
export class DefaultInstitutionStatistics extends GlobalInstitutionStatistics {
  override chartCards() {
    return [
      this.renderParticipantsCard(),
      this.renderTotalAppointmentsCard(),
      this.renderParticipatingParticipationCountCard(),
      this.renderParticipatedParticipationCountCard(),
      // this.renderParticipatingAndParticipatedParticipationCountCard(),
      this.renderSuspendedAndSignedOffAndWaitingParticipationCountCard(),
      this.renderAverageParticipationAndParticipatedParticipationsPerUserCard(),
      this.renderAverageSuspendedAndSignedOffAndWaitingParticipationsPerUser(),
      this.renderTotalWorkshopsCard(),
      // html`<br />`,
      this.renderProductRegistrationsOverTimeCard(),
      this.renderParticipationOverTimeCard(),
      this.renderParticipationsCountPerStateCard(),
    ];
  }
}

export const defaultInstitutionStatisticsPlugin: InstitutionStatisticsPlugin = {
  id: 'institution-statistics::default',
  type: 'institution-statistics',
  render: (filter: BHFilter) => {
    return html`<default-institution-statistics .filter=${filter}></default-institution-statistics>`;
  },
};
